import NextLink from 'next/link';
import React from 'react';
import PropTypes from 'prop-types';
import Pagination from '@mui/material/Pagination';
import { Link, Box } from '@mui/material';
import { AddCircle } from '@mui/icons-material';
import Breadcrumb from '../Breadcrumb';
import buildTemplateImageUrl from 'utils/buildTemplateImageUrl';
import { useStyles } from './styles';
import { useRouter } from 'next/router';

const Props = {
  breadcrumbItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      path: PropTypes.string,
    }),
  ).isRequired,
  formatName: PropTypes.string,
  templates: PropTypes.shape({
    total: PropTypes.number.isRequired,
    perPage: PropTypes.number.isRequired,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        theme_slug: PropTypes.string.isRequired,
        theme_image_prefix: PropTypes.string.isRequired,
        theme_name: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  columns: PropTypes.number.isRequired,
  expandButtonUrl: PropTypes.string,
  currentPage: PropTypes.number,
  onPaginationChange: PropTypes.func,
  showBreadcrumbs: PropTypes.bool,
};

const DefaultProps = {
  showBreadcrumbs: true,
};

export default function List({
  breadcrumbItems,
  formatName,
  templates,
  columns,
  expandButtonUrl,
  currentPage,
  onPaginationChange,
  showBreadcrumbs,
  showH1Heading = '',
  showH1Description = '',
}) {
  const classes = useStyles({ columns });
  const paginationCount = Math.ceil(templates.total / templates.perPage);
  const { asPath, pathname } = useRouter();
  const slugPage = pathname === '/infographic-templates/[slug]';

  let pageFormat = asPath.includes('format');

  return (
    <>
      {!!expandButtonUrl && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Breadcrumb showLinkHome={false} items={breadcrumbItems} />

          <NextLink href={expandButtonUrl} passHref={true}>
            <Link className={classes.btnSeeMoreMobile} underline="hover">
              See More
              <Box ml={1}>
                <AddCircle fontSize="small" />
              </Box>
            </Link>
          </NextLink>
        </Box>
      )}

      {pageFormat && showBreadcrumbs && <Breadcrumb items={breadcrumbItems} showLinkHome={false} />}

      {showH1Heading && <h1>{showH1Heading}</h1>}
      {showH1Description && <p>{showH1Description}</p>}

      <div className={!pageFormat ? classes.root : classes.forMobile}>
        {templates.data.map((template, index) => (
          <div
            key={index}
            className={!pageFormat && !slugPage ? classes.item : classes.itemForMobile}
          >
            <div className={classes.thumb}>
              <NextLink
                href={`/infographic-templates/${template.theme_slug}`}
                prefetch={false}
                passHref={true}
              >
                <Link className={classes.thumbLink} underline="hover">
                  Preview
                </Link>
              </NextLink>

              <img
                className={!slugPage ? classes.thumbImage : classes.imageSlugPage}
                src={buildTemplateImageUrl(template.theme_slug, template.theme_image_prefix)}
                alt={formatName ? `${formatName} template preview` : template.theme_name}
              />
            </div>
          </div>
        ))}
      </div>

      {currentPage !== undefined && paginationCount > 1 && (
        <Pagination
          className={classes.pagination}
          page={currentPage}
          count={paginationCount}
          onChange={(event, newPage) => onPaginationChange(newPage)}
        />
      )}
    </>
  );
}

List.propTypes = Props;
List.defaultProps = DefaultProps;
